import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, DatePicker, message, Space, Typography, Badge, Row, Col, Statistic, Upload, Tabs, Empty } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined, SendOutlined, FileTextOutlined, UploadOutlined, PaperClipOutlined, LinkOutlined, DownloadOutlined, EditOutlined, YoutubeOutlined, NotificationOutlined, HistoryOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Text, Title } = Typography;

const priorityColors = { low: 'green', medium: 'blue', high: 'orange', urgent: 'red' };
const statusColors = { open: 'processing', in_progress: 'warning', done: 'success', reopened: 'error' };
const statusLabels = { open: 'Open', in_progress: 'In Progress', done: 'Done', reopened: 'Reopened' };

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');

// Bainsla Music staff work channel-wise; other companies (Intube Media etc.) keep the plain task flow.
const isChannelCompany = (name) => /bainsla/i.test(name || '');

export default function StaffTasks({ user }) {
  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'hr_manager';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [myTaskModal, setMyTaskModal] = useState(false);
  const [doneModal, setDoneModal] = useState(null);
  const [reopenModal, setReopenModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [addForm] = Form.useForm();
  const [myTaskForm] = Form.useForm();
  const [doneForm] = Form.useForm();
  const [reopenForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filterStatus, setFilterStatus] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [channels, setChannels] = useState([]);
  const [staffChannels, setStaffChannels] = useState([]);
  const [assignStaffId, setAssignStaffId] = useState(null);
  const [chTaskModal, setChTaskModal] = useState(false);
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [batches, setBatches] = useState([]);
  const [chTaskForm] = Form.useForm();
  const [broadcastForm] = Form.useForm();

  const prevDoneCountRef = useRef(null);
  const prevOpenCountRef = useRef(null);

  const fetchTasks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const url = canManage ? '/staff/tasks' : '/staff/my-tasks';
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get(url, { params });
      const newTasks = Array.isArray(res.data) ? res.data : [];

      const newOpenCount = newTasks.filter(t => t.status === 'open' || t.status === 'reopened').length;
      const newDoneCount = newTasks.filter(t => t.status === 'done').length;

      // Show notification only if counts genuinely changed
      if (prevOpenCountRef.current !== null && newOpenCount > prevOpenCountRef.current) {
        message.info({ content: `${newOpenCount - prevOpenCountRef.current} new task(s)!`, duration: 4, key: 'new-task' });
      }
      if (canManage && prevDoneCountRef.current !== null && newDoneCount > prevDoneCountRef.current) {
        message.success({ content: 'Task marked as Done!', duration: 3, key: 'task-done' });
      }

      prevOpenCountRef.current = newOpenCount;
      prevDoneCountRef.current = newDoneCount;
      setTasks(newTasks);
    } catch (e) { console.error(e); }
    if (!silent) setLoading(false);
  }, [canManage, filterStatus]);

  const fetchStaff = async () => {
    try {
      const res = await api.get('/staff');
      setStaffList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {}
  };

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(Array.isArray(res.data) ? res.data : []); } catch (e) {}
  };

  const fetchChannels = async () => {
    try { const res = await api.get('/channels'); setChannels(Array.isArray(res.data) ? res.data : []); } catch (e) {}
  };

  const fetchBatches = async () => {
    try { const res = await api.get('/staff/task-batches'); setBatches(Array.isArray(res.data) ? res.data : []); } catch (e) {}
  };

  // Channels already assigned to this staff member (channel-wise task picker)
  const loadStaffChannels = async (staffId) => {
    setAssignStaffId(staffId);
    setStaffChannels([]);
    if (!staffId) return;
    try {
      const res = await api.get('/staff/assignments', { params: { staff_id: staffId } });
      const row = (Array.isArray(res.data) ? res.data : []).find(s => String(s.staff_id) === String(staffId));
      setStaffChannels(row?.channels || []);
    } catch (e) {}
  };

  useEffect(() => {
    fetchTasks();
    if (canManage) { fetchStaff(); fetchCompanies(); fetchChannels(); fetchBatches(); }
    const poll = setInterval(() => fetchTasks(true), 15000);
    return () => clearInterval(poll);
  }, [fetchTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTask = async (vals) => {
    try {
      setUploading(true);
      const channelRows = (vals.channels || [])
        .filter(c => c && c.channel_id)
        .map(c => ({ channel_id: c.channel_id, work: c.work || '' }));
      const res = await api.post('/staff/task', {
        assigned_to: vals.assigned_to,
        title: vals.title,
        description: vals.description,
        priority: vals.priority,
        due_date: vals.due_date ? vals.due_date.format('YYYY-MM-DD') : null,
        channels: channelRows,
      });
      const taskId = res.data?.id;
      if (uploadFile && taskId) {
        const formData = new FormData();
        formData.append('task_id', taskId);
        formData.append('file', uploadFile);
        await api.post('/staff/task-attachment', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      message.success('Task assigned!');
      setAddModal(false);
      setUploadFile(null);
      setAssignStaffId(null);
      setStaffChannels([]);
      addForm.resetFields();
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
    setUploading(false);
  };

  // Channel se seedha task: jis-jis staff ko wo channel assigned hai, sabko chala jayega
  const addChannelTask = async (vals) => {
    try {
      setUploading(true);
      const res = await api.post('/staff/channel-task', {
        channel_id: vals.channel_id,
        work: vals.work,
        priority: vals.priority,
        due_date: vals.due_date ? vals.due_date.format('YYYY-MM-DD') : null,
      });
      message.success(res.data?.message || 'Task sent');
      setChTaskModal(false);
      chTaskForm.resetFields();
      fetchTasks(); fetchBatches();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
    setUploading(false);
  };

  // Broadcast: ek hi message company ke saare staff ko (WhatsApp jaisa)
  const sendBroadcast = async (vals) => {
    try {
      setUploading(true);
      const res = await api.post('/staff/broadcast-task', {
        company_id: vals.company_id,
        title: vals.title,
        description: vals.description,
        priority: vals.priority,
        due_date: vals.due_date ? vals.due_date.format('YYYY-MM-DD') : null,
      });
      message.success(res.data?.message || 'Sent');
      setBroadcastModal(false);
      broadcastForm.resetFields();
      fetchTasks(); fetchBatches();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
    setUploading(false);
  };

  const markChannelDone = async (item) => {
    try {
      await api.post('/staff/task-channel-done', { item_id: item.id });
      message.success(`${item.channel_name} ka kaam done`);
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const addMyTask = async (vals) => {
    try {
      await api.post('/staff/my-task', {
        title: vals.title,
        description: vals.description,
        priority: vals.priority,
        due_date: vals.due_date ? vals.due_date.format('YYYY-MM-DD') : null,
      });
      message.success('Apna task ban gaya!');
      setMyTaskModal(false);
      myTaskForm.resetFields();
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const deleteMyTask = async (id) => {
    try {
      await api.delete(`/staff/my-task/${id}`);
      message.success('Task deleted');
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const markDone = async (vals) => {
    try {
      await api.post('/staff/task-done', { id: doneModal.id, staff_comment: vals.staff_comment });
      message.success('Task marked as done!');
      setDoneModal(null);
      doneForm.resetFields();
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const reopenTask = async (vals) => {
    try {
      await api.post('/staff/task-reopen', { id: reopenModal.id, admin_comment: vals.admin_comment });
      message.success('Task reopened!');
      setReopenModal(null);
      reopenForm.resetFields();
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const editTask = async (vals) => {
    try {
      await api.put(`/staff/task/${editModal.id}`, {
        title: vals.title,
        description: vals.description,
        priority: vals.priority,
        due_date: vals.due_date ? vals.due_date.format('YYYY-MM-DD') : null,
      });
      message.success('Task updated!');
      setEditModal(null);
      editForm.resetFields();
      fetchTasks();
    } catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/staff/task/${id}`);
      message.success('Task deleted');
      fetchTasks();
    } catch (e) { message.error('Failed'); }
  };

  const renderWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => urlRegex.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}><LinkOutlined /> {part.length > 50 ? part.substring(0, 50) + '...' : part}</a>
      : <span key={i}>{part}</span>
    );
  };

  const openCount = tasks.filter(t => t.status === 'open' || t.status === 'reopened').length;
  const doneCount = tasks.filter(t => t.status === 'done').length;

  const isSelfTask = (r) => String(r.assigned_by) === String(user?.id) && String(r.assigned_to) === String(user?.id);

  const staffById = React.useMemo(() => Object.fromEntries(staffList.map(s => [String(s.id), s])), [staffList]);
  const selectedStaff = assignStaffId ? staffById[String(assignStaffId)] : null;
  const channelMode = isChannelCompany(selectedStaff?.company_name);
  // Prefer the staff member's own assigned channels; fall back to every channel of their company
  const pickerChannels = staffChannels.length
    ? staffChannels.map(c => ({ id: c.channel_id, channel_name: c.channel_name, company_name: c.company_name }))
    : channels.filter(c => !selectedStaff?.company_id || String(c.company_id) === String(selectedStaff.company_id));

  const visibleTasks = companyFilter
    ? tasks.filter(t => String(staffById[String(t.assigned_to)]?.company_id || '') === String(companyFilter))
    : tasks;

  const renderChannelRows = (r) => {
    if (!r.channels || !r.channels.length) return null;
    return (
      <div style={{ marginTop: 6 }}>
        {r.channels.map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '4px 8px', marginBottom: 4, background: c.status === 'done' ? '#f6ffed' : '#fafafa', borderRadius: 6 }}>
            <Tag color="red" style={{ margin: 0 }}><YoutubeOutlined /> {c.channel_name || `#${c.channel_id}`}</Tag>
            <span style={{ flex: 1, fontSize: 12 }}>{renderWithLinks(c.work)}</span>
            {c.status === 'done'
              ? <Tag color="green" style={{ margin: 0 }}><CheckCircleOutlined /> Done</Tag>
              : (!canManage && (r.status === 'open' || r.status === 'reopened')
                  ? <Button size="small" type="link" onClick={() => markChannelDone(c)}>Done</Button>
                  : null)}
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    { title: '#', render: (_, __, i) => i + 1, width: 48 },
    { title: 'Task', dataIndex: 'title', key: 'title', width: 340, render: (t, r) => (
      <div>
        <Text strong style={{ fontSize: 14 }}>{t}</Text>
        {isSelfTask(r) && <Tag color="purple" style={{ marginLeft: 6 }}>My Task</Tag>}
        {r.task_type === 'broadcast' && <Tag color="volcano" style={{ marginLeft: 6 }}><NotificationOutlined /> All Staff</Tag>}
        {r.description && <div style={{ marginTop: 2 }}><Text type="secondary" style={{ fontSize: 12 }}>{renderWithLinks(r.description)}</Text></div>}
        {renderChannelRows(r)}
        {r.attachment_path && (
          <div style={{ marginTop: 4 }}>
            <a href={`${API_BASE}/${r.attachment_path}`} target="_blank" rel="noopener noreferrer">
              <Tag icon={<PaperClipOutlined />} color="blue">Attachment <DownloadOutlined /></Tag>
            </a>
          </div>
        )}
        {r.admin_comment && r.status === 'reopened' && <div style={{ marginTop: 4 }}><Tag color="red">Admin: {renderWithLinks(r.admin_comment)}</Tag></div>}
        {r.staff_comment && r.status === 'done' && <div style={{ marginTop: 4 }}><Tag color="green">Staff: {renderWithLinks(r.staff_comment)}</Tag></div>}
      </div>
    )},
    ...(canManage ? [{ title: 'Assigned To', dataIndex: 'staff_name', key: 'staff', render: (n, r) => (
      <div>
        <div>{n || r.staff_username}</div>
        {staffById[String(r.assigned_to)]?.company_name && <Tag color="geekblue" style={{ marginTop: 2 }}>{staffById[String(r.assigned_to)].company_name}</Tag>}
      </div>
    ) }] : []),
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90, render: p => <Tag color={priorityColors[p]}>{(p || 'medium').toUpperCase()}</Tag> },
    { title: 'Due Date', dataIndex: 'due_date', key: 'due', width: 100, render: d => d || '-' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100, render: s => <Badge status={statusColors[s]} text={statusLabels[s]} /> },
    { title: 'Created', dataIndex: 'created_at', key: 'created', width: 130, render: d => d ? dayjs(d).format('DD MMM, hh:mm A') : '-' },
    { title: 'Actions', key: 'actions', width: 220, render: (_, r) => (
      <Space size="small" wrap>
        {(!canManage || isSelfTask(r)) && (r.status === 'open' || r.status === 'reopened') && (
          <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => { setDoneModal(r); doneForm.resetFields(); }}>Done</Button>
        )}
        {!canManage && isSelfTask(r) && (
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({ title: 'Apna ye task delete karein?', onOk: () => deleteMyTask(r.id) })} />
        )}
        {canManage && (
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditModal(r);
            editForm.setFieldsValue({
              title: r.title,
              description: r.description,
              priority: r.priority,
              due_date: r.due_date ? dayjs(r.due_date) : null,
            });
          }}>Edit</Button>
        )}
        {canManage && r.status !== 'reopened' && (
          <Button size="small" danger icon={<ReloadOutlined />} onClick={() => { setReopenModal(r); reopenForm.resetFields(); }}>Reopen</Button>
        )}
        {canManage && (
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({ title: 'Delete this task?', onOk: () => deleteTask(r.id) })} />
        )}
      </Space>
    )},
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><FileTextOutlined /> {canManage ? 'Staff Tasks' : 'My Tasks'}</Title>
        </Col>
        <Col>
          <Space wrap>
            <Select value={filterStatus} onChange={v => setFilterStatus(v)} style={{ width: 130 }} allowClear placeholder="All Status">
              <Select.Option value="open">Open</Select.Option>
              <Select.Option value="reopened">Reopened</Select.Option>
              <Select.Option value="done">Done</Select.Option>
            </Select>
            {canManage && (
              <Select value={companyFilter} onChange={v => setCompanyFilter(v ?? null)} style={{ width: 200 }} allowClear placeholder="All Companies"
                options={companies.map(c => ({ value: c.id, label: c.name }))} />
            )}
            {canManage && <Button icon={<YoutubeOutlined />} onClick={() => { setChTaskModal(true); chTaskForm.resetFields(); }}>Channel se Task</Button>}
            {canManage && <Button icon={<NotificationOutlined />} onClick={() => { setBroadcastModal(true); broadcastForm.resetFields(); }}>Broadcast</Button>}
            {canManage && <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddModal(true); addForm.resetFields(); setUploadFile(null); setAssignStaffId(null); setStaffChannels([]); }}>Assign Task</Button>}
            <Button type={canManage ? 'default' : 'primary'} icon={<PlusOutlined />} onClick={() => { setMyTaskModal(true); myTaskForm.resetFields(); }}>Create My Task</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <Card style={{ borderRadius: 10, background: '#fff7e6', textAlign: 'center' }}>
            <Statistic title="Open / Reopened" value={openCount} prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card style={{ borderRadius: 10, background: '#f6ffed', textAlign: 'center' }}>
            <Statistic title="Done" value={doneCount} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, background: '#e6f7ff', textAlign: 'center' }}>
            <Statistic title="Total Tasks" value={tasks.length} prefix={<FileTextOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        {canManage ? (
          <Tabs
            defaultActiveKey="tasks"
            onChange={k => { if (k === 'history') fetchBatches(); }}
            items={[
              {
                key: 'tasks',
                label: <span><FileTextOutlined /> Tasks</span>,
                children: (
                  <Table dataSource={visibleTasks} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} size="middle"
                    scroll={{ x: 1000 }}
                    rowClassName={r => r.status === 'reopened' ? 'ant-table-row-warning' : ''} />
                ),
              },
              {
                key: 'history',
                label: <span><HistoryOutlined /> Bheje gaye Task (History)</span>,
                children: batches.length ? (
                  <Table dataSource={batches} rowKey="batch_id" size="middle" pagination={{ pageSize: 15 }} scroll={{ x: 900 }}
                    columns={[
                      { title: 'Type', dataIndex: 'task_type', width: 110, render: t => t === 'broadcast'
                        ? <Tag color="volcano"><NotificationOutlined /> Broadcast</Tag>
                        : <Tag color="red"><YoutubeOutlined /> Channel</Tag> },
                      { title: 'Task / Message', dataIndex: 'title', render: (t, r) => (
                        <div>
                          <Text strong>{t}</Text>
                          {r.description && <div><Text type="secondary" style={{ fontSize: 12 }}>{renderWithLinks(r.description)}</Text></div>}
                        </div>
                      )},
                      { title: 'Staff', dataIndex: 'staff_names', width: 240, render: v => <Text style={{ fontSize: 12 }}>{v}</Text> },
                      { title: 'Done', width: 100, render: (_, r) => <Tag color={Number(r.done_count) >= Number(r.staff_count) ? 'green' : 'orange'}>{r.done_count}/{r.staff_count}</Tag> },
                      { title: 'Bheja', dataIndex: 'assigned_by_name', width: 140 },
                      { title: 'Kab', dataIndex: 'created_at', width: 150, render: d => d ? dayjs(d).format('DD MMM, hh:mm A') : '-' },
                    ]} />
                ) : <Empty description="Abhi koi channel task / broadcast nahi bheja" />,
              },
            ]}
          />
        ) : (
          <Table dataSource={tasks} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} size="middle"
            scroll={{ x: 1000 }}
            rowClassName={r => r.status === 'reopened' ? 'ant-table-row-warning' : ''} />
        )}
      </Card>

      {/* Add Task Modal (Admin/HR) */}
      <Modal title="Assign New Task" open={addModal} onCancel={() => setAddModal(false)} footer={null} width={520}>
        <Form form={addForm} layout="vertical" onFinish={addTask} initialValues={{ priority: 'medium' }}>
          <Form.Item name="assigned_to" label="Assign To (company-wise)" rules={[{ required: true, message: 'Select staff' }]}>
            <Select placeholder="Select staff" showSearch optionFilterProp="label"
              onChange={(v) => { loadStaffChannels(v); addForm.setFieldsValue({ channels: [] }); }}
              options={[
                ...companies.map(co => ({
                  label: co.name,
                  options: staffList.filter(s => String(s.company_id) === String(co.id))
                    .map(s => ({ value: s.id, label: `${s.full_name || s.username}${s.staff_id ? ` (${s.staff_id})` : ''}${s.role === 'hr_manager' ? ' [HR]' : ''}` })),
                })).filter(g => g.options.length),
                {
                  label: 'No Company',
                  options: staffList.filter(s => !s.company_id)
                    .map(s => ({ value: s.id, label: `${s.full_name || s.username}${s.staff_id ? ` (${s.staff_id})` : ''}${s.role === 'hr_manager' ? ' [HR]' : ''}` })),
                },
              ].filter(g => g.options.length)} />
          </Form.Item>

          {channelMode && (
            <div style={{ background: '#fff7e6', padding: 10, borderRadius: 8, marginBottom: 12 }}>
              <Text strong><YoutubeOutlined /> Channel-wise kaam ({selectedStaff?.company_name})</Text>
              <div><Text type="secondary" style={{ fontSize: 12 }}>Channel chuno aur uske saamne uska kaam likho. Kai channel ek hi task me daal sakte ho.</Text></div>
              <Form.List name="channels">
                {(fields, { add, remove }) => (
                  <div style={{ marginTop: 8 }}>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row key={key} gutter={8} style={{ marginBottom: 8 }} align="top">
                        <Col xs={24} sm={9}>
                          <Form.Item {...rest} name={[name, 'channel_id']} rules={[{ required: true, message: 'Channel chuno' }]} style={{ marginBottom: 0 }}>
                            <Select showSearch optionFilterProp="label" placeholder="Channel"
                              options={pickerChannels.map(c => ({ value: c.id, label: c.channel_name }))} />
                          </Form.Item>
                        </Col>
                        <Col xs={22} sm={13}>
                          <Form.Item {...rest} name={[name, 'work']} rules={[{ required: true, message: 'Kaam likho' }]} style={{ marginBottom: 0 }}>
                            <Input.TextArea rows={2} placeholder="Is channel par ye-ye karna hai..." />
                          </Form.Item>
                        </Col>
                        <Col xs={2} sm={2}>
                          <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                        </Col>
                      </Row>
                    ))}
                    <Button size="small" icon={<PlusOutlined />} onClick={() => add()} block>Channel add karo</Button>
                  </div>
                )}
              </Form.List>
            </div>
          )}

          <Form.Item name="title" label={channelMode ? 'Task Title (optional — channel wise kaam likha ho to zaroori nahi)' : 'Task Title'}
            rules={channelMode ? [] : [{ required: true, message: 'Enter task title' }]}>
            <Input placeholder="What needs to be done?" />
          </Form.Item>
          <Form.Item name="description" label="Description / Links" extra="Paste Drive links, YouTube links, or any URL here">
            <Input.TextArea rows={3} placeholder="Task details, paste links here (Google Drive, YouTube, etc.)..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Attachment (Photo/PDF)">
            <Upload
              beforeUpload={file => { setUploadFile(file); return false; }}
              onRemove={() => setUploadFile(null)}
              fileList={uploadFile ? [uploadFile] : []}
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} block loading={uploading}>Assign Task</Button>
        </Form>
      </Modal>

      {/* Channel se seedha Task (channel search → uske saare assigned staff) */}
      <Modal title={<span><YoutubeOutlined /> Channel se Task</span>} open={chTaskModal} onCancel={() => setChTaskModal(false)} footer={null} width={520}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Channel search karo aur kaam likho — jis-jis staff ko wo channel assigned hai, sabke dashboard me task chala jayega.
        </Text>
        <Form form={chTaskForm} layout="vertical" onFinish={addChannelTask} initialValues={{ priority: 'medium' }} style={{ marginTop: 12 }}>
          <Form.Item name="channel_id" label="Channel" rules={[{ required: true, message: 'Channel chuno' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Channel search karo"
              options={channels.map(c => ({ value: c.id, label: `${c.channel_name}${c.company_name ? ` (${c.company_name})` : ''}` }))} />
          </Form.Item>
          <Form.Item name="work" label="Is channel ka kaam" rules={[{ required: true, message: 'Kaam likho' }]} extra="Link bhi daal sakte ho (Drive, YouTube, gaana ka link)">
            <Input.TextArea rows={4} placeholder="Is channel par ye-ye karna hai..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} block loading={uploading}>Send to assigned staff</Button>
        </Form>
      </Modal>

      {/* Broadcast (WhatsApp jaisa) — company ke saare staff ko */}
      <Modal title={<span><NotificationOutlined /> Broadcast — company ke saare staff ko</span>} open={broadcastModal} onCancel={() => setBroadcastModal(false)} footer={null} width={520}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Ek hi baar likho — us company ke saare staff ke dashboard me turant dikh jayega. History me record rahega (kisko kab kya diya, kaun done kiya).
        </Text>
        <Form form={broadcastForm} layout="vertical" onFinish={sendBroadcast} initialValues={{ priority: 'medium' }} style={{ marginTop: 12 }}>
          <Form.Item name="company_id" label="Company" rules={[{ required: true, message: 'Company chuno' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Jaise Bainsla Music"
              options={companies.map(c => ({ value: c.id, label: `${c.name} (${staffList.filter(s => String(s.company_id) === String(c.id)).length} staff)` }))} />
          </Form.Item>
          <Form.Item name="title" label="Message / Task" rules={[{ required: true, message: 'Kuch likho' }]}>
            <Input placeholder="Jaise: Ye gaana in channels par use karna hai" />
          </Form.Item>
          <Form.Item name="description" label="Detail / Link" extra="Gaana ka link, Drive link, ya koi bhi URL — staff ke dashboard me clickable dikhega">
            <Input.TextArea rows={4} placeholder="Link aur detail yahan daalo..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} block loading={uploading}>Send to all staff</Button>
        </Form>
      </Modal>

      {/* Create My Task Modal (Staff self-assigned) */}
      <Modal title="Apna Task Banaye" open={myTaskModal} onCancel={() => setMyTaskModal(false)} footer={null} width={520}>
        <Form form={myTaskForm} layout="vertical" onFinish={addMyTask} initialValues={{ priority: 'medium' }}>
          <Form.Item name="title" label="Task Title" rules={[{ required: true, message: 'Task ka naam likho' }]}>
            <Input placeholder="Kya karna hai?" />
          </Form.Item>
          <Form.Item name="description" label="Description / Links" extra="Details ya koi link (Drive, YouTube) yahan daal sakte ho">
            <Input.TextArea rows={3} placeholder="Task ki detail, links..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} block>Create Task</Button>
        </Form>
      </Modal>

      {/* Edit Task Modal (Admin/HR) */}
      <Modal title="Edit Task" open={!!editModal} onCancel={() => setEditModal(null)} footer={null} width={500}>
        <Form form={editForm} layout="vertical" onFinish={editTask}>
          <Form.Item name="title" label="Task Title" rules={[{ required: true, message: 'Enter task title' }]}>
            <Input placeholder="Task title" />
          </Form.Item>
          <Form.Item name="description" label="Description / Links" extra="Edit description, paste new links">
            <Input.TextArea rows={3} placeholder="Task details, links..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" icon={<EditOutlined />} block>Save Changes</Button>
        </Form>
      </Modal>

      {/* Mark Done Modal (Staff) — with proof/links */}
      <Modal title="Complete Task" open={!!doneModal} onCancel={() => setDoneModal(null)} footer={null} width={500}>
        {doneModal && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Task: </Text><Text>{doneModal.title}</Text>
            {doneModal.description && <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>{renderWithLinks(doneModal.description)}</Text></div>}
            {doneModal.attachment_path && (
              <div style={{ marginTop: 4 }}>
                <a href={`${API_BASE}/${doneModal.attachment_path}`} target="_blank" rel="noopener noreferrer">
                  <Tag icon={<PaperClipOutlined />} color="blue">View Attachment</Tag>
                </a>
              </div>
            )}
            {doneModal.admin_comment && doneModal.status === 'reopened' && (
              <div style={{ marginTop: 8, padding: 8, background: '#fff2f0', borderRadius: 6 }}>
                <Text type="danger"><strong>Admin Note:</strong> {doneModal.admin_comment}</Text>
              </div>
            )}
          </div>
        )}
        <Form form={doneForm} layout="vertical" onFinish={markDone}>
          <Form.Item name="staff_comment"
            label={doneModal && isSelfTask(doneModal) ? 'Notes / Links (optional)' : 'Proof / Notes / Links (required)'}
            rules={doneModal && isSelfTask(doneModal) ? [] : [{ required: true, message: 'Please write what you did or paste proof links' }]}
            extra="Write what you did, paste links as proof (Drive, YouTube, etc.)">
            <Input.TextArea rows={4} placeholder="Describe what you completed, paste proof links here..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />} block>Mark as Done</Button>
        </Form>
      </Modal>

      {/* Reopen Task Modal (Admin/HR) */}
      <Modal title="Reopen Task" open={!!reopenModal} onCancel={() => setReopenModal(null)} footer={null}>
        {reopenModal && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Task: </Text><Text>{reopenModal.title}</Text>
            {reopenModal.staff_comment && (
              <div style={{ marginTop: 8, padding: 8, background: '#f6ffed', borderRadius: 6 }}>
                <Text><strong>Staff said:</strong> {reopenModal.staff_comment}</Text>
              </div>
            )}
          </div>
        )}
        <Form form={reopenForm} layout="vertical" onFinish={reopenTask}>
          <Form.Item name="admin_comment" label="What's the issue? (required)" rules={[{ required: true, message: 'Please explain the issue' }]}>
            <Input.TextArea rows={3} placeholder="Explain what needs to be fixed..." />
          </Form.Item>
          <Button type="primary" danger htmlType="submit" icon={<ReloadOutlined />} block>Reopen Task</Button>
        </Form>
      </Modal>
    </div>
  );
}
